import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Auth} from 'aws-amplify';
import {hideLoader, showLoader} from '../../store/common/slice';
import Button from '../../common/Button';
import {constants} from '../../common/constants/constants';
import creditCard from '../../images/newDesign/creditCard.svg';
import arrowInCircle from '../../images/newDesign/arrow_in_white_circle.svg';
import listIcon from '../../images/newDesign/listIcon.svg';
import chatIcon from '../../images/newDesign/chatIcon.svg';
import PaymentFlowContainer from './PaymentFlowContainer';
import {resetTenant, setBuildingData} from "../../store/tenant/slice";
import useBodyClass from "../../common/hooks/useBodyClassName";


export function BuildingPayment({ match }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();
    const { buildingID } = match.params;

    const buildingFeatures = useSelector(state => state.buildings.currentBuildingFeatures);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const [isViewedAsManager, setIsViewedAsManager] = useState(false);

    document.title = t('titles.building_profile');

    const externalPartnerId = currentBuildingData?.managementCompany?.externalId;

    const allowedExternalPartnerIds = constants.newFlowBuildingsExclude; // requested by rayee via email
    const isPrivatePartner = externalPartnerId === null

    const isEntityAllowed = (!allowedExternalPartnerIds.includes(externalPartnerId) || isPrivatePartner);

    const tenantDetailsUrl = isEntityAllowed && !isViewedAsManager ? `/${constants.pages.choosePaymentMethod}/${buildingID}` : (
          isViewedAsManager ? `/${constants.pages.tenantDetailsManagerUrl}/${buildingID}` :
                              `/${constants.pages.tenantDetailsUrl}/${buildingID}`
    );
    const tenantMaintenance = `/${constants.pages.maintenanceTenantUrl}/${buildingID}`;
    const contactManager = `/${constants.pages.managerDetails}/${buildingID}`;

    function handlePayClick() {
        updateBuildingData();
        history.push({ pathname: tenantDetailsUrl });
    }

    function handleTasksClick() {
        history.push({ pathname: tenantMaintenance });
    }
    function handleContactManagerClick() {
        history.push({ pathname: contactManager });
    }

    useEffect(async () => {
        dispatch(showLoader());
        try {
            await Auth.currentAuthenticatedUser()
            setIsViewedAsManager(true);
        } catch(e) {
            setIsViewedAsManager(false)
        }
        dispatch(hideLoader());
    }, []);

    useEffect(() => {
        // clean tenant details on component load to prevent cached data displaying
        dispatch(resetTenant());
    }, []);

    useEffect(updateBuildingData, [buildingFeatures, currentBuildingData])

    const showBuildingMaintenance = useMemo(() => buildingFeatures?.['maintenanceLivy'] != null ?? false, [buildingFeatures]);

    useBodyClass('payment-flow');
    document.title = t('titles.building_profile');


    function updateBuildingData() {
        dispatch(setBuildingData({
            buildingID,
            address: currentBuildingData?.address,
            currency: currentBuildingData?.currency,
            apartments: currentBuildingData?.apartments,
            assignedToNextMonth: currentBuildingData?.assignedToNextMonth,
            numberOfInstallmentsForDebt: currentBuildingData?.numberOfInstallmentsForDebt,
            tenantCommission: currentBuildingData?.tenantCommission,
            disableCreditCard: buildingFeatures?.disableCreditCard === 'true',
            disableBankCharge: buildingFeatures?.disableBankCharge === 'true',
            // SSB-1236 enabled on advacned terminal also 
            disableBitPayments: buildingFeatures?.disableBitPay === 'true',
            disableMobilePayments: buildingFeatures?.disableMobilePay === 'true',
            // https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=62dd3b4b3257defaf50c83b7&selectedIssue=SSB-850
            // default being reversed without reversing the name in order to save time on BE
            enableCashPayments: buildingFeatures?.disableCashPayments === 'true',
            enableChequePayments: buildingFeatures?.disableChequePayments === 'true',
            disableMarkAsPaid: buildingFeatures?.disableMarkAsPaid === 'true',
            disableInsurancePage: buildingFeatures?.disableInsurancePage === 'true',
        }));
    }

    return (
        <PaymentFlowContainer hideBack title={t('payments.welcome')} subheader={t('payments.choose_action')}>
            <div className='credit-card-and-actions-block'>
                <img src={creditCard} className="credit-card-img" />

                <div className='actions-block'>
                    <Button onClick={handlePayClick}>
                        {t('payments.proceed_to_payment')} <img className='arrow' src={arrowInCircle} />
                    </Button>
                    <Button outlined onClick={handleTasksClick} className='btn-bordered' disabled={!showBuildingMaintenance}>
                        <img className='icon' src={listIcon} /> {t('payments.proceed_to_tasks')}
                    </Button>
                    <Button outlined onClick={handleContactManagerClick} className='btn-bordered'>
                        <img className='icon' src={chatIcon} /> {t('payments.contact_manager')}
                    </Button>
                </div>
            </div>
        </PaymentFlowContainer>
    )
}
